class CategoryStateLayer {
  constructor () {
    this.state = {
      activeFacets: [],
      activeSort: null,
      facets: [],
      lastSelectedDetail: null,
      pagination: {
        currentPage: 1,
        offset: window.theme.collection.limit,
      },
      products: [],
      totalProducts: null,
    }

    // Bundle anchors loading state
    this.bundleAnchorsReady = !window.theme.collection?.bundle
      ?.anchorCardsEnabled
    this.pendingProducts = null

    this.subscribeToEvents()
    this.setupBundleAnchorsListener()
  }

  setupBundleAnchorsListener () {
    // Listen for bundle anchors to be loaded using BAO event system
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.BUNDLE.ANCHORS_LOADED,
      this.handleBundleAnchorsLoaded.bind(this),
    )
  }

  handleBundleAnchorsLoaded (anchors) {
    this.bundleAnchorsReady = true

    // If we have pending products, apply bundle management now
    if (this.pendingProducts) {
      this.applyBundleManagementToPendingProducts()
    }
  }

  applyBundleManagementToPendingProducts () {
    if (this.pendingProducts) {
      // Apply bundle card management to pending products
      const managedProducts = this.applyBundleCardManagement(
        this.pendingProducts,
      )
      this.state.products = managedProducts

      window.theme.category.eventBus.publish(
        'state:products:updated',
        managedProducts,
      )

      this.pendingProducts = null
    }
  }

  /**
   * Updates a URL parameter and pushes the new state to history
   * @param {string} paramName - The name of the parameter to update
   * @param {string|null} paramValue - The value to set, or null to remove the parameter
   */
  updateUrlParameter (paramName, paramValue) {
    const url = new URL(window.location.href)
    if (paramValue) {
      url.searchParams.set(paramName, paramValue)
    } else {
      url.searchParams.delete(paramName)
    }
    window.history.pushState({ path: url.toString() }, '', url.toString())
  }

  /**
   * Updates the filters URL parameter based on the current activeFacets
   */
  updateFiltersUrlParameter () {
    const filtersString = this.activeFacets
      .map(
        facet =>
          `f-${facet.key}=${facet.values
            .map(value => encodeURIComponent(value))
            .join(',')}`,
      )
      .join('&')

    this.updateUrlParameter('filters', filtersString || null)
  }

  subscribeToEvents () {
    const eventBus = window.theme.category.eventBus

    eventBus.subscribe('api:products:fetched', response => {
      // Store the API total before setting products (which may filter and update total)
      const apiTotal = response.queryResults[0].meta.totalResultsFound
      const apiProducts = response.queryResults[0].records

      // Check if products are already pre-filtered (from cache)
      if (response.isPreFiltered) {
        // Set products directly without bundle management
        this.state.products = apiProducts
        this.totalProducts = apiTotal
        window.theme.category.eventBus.publish(
          'state:products:updated',
          apiProducts,
        )
      } else {
        // Normal processing - products come from either API or cache
        this.products = apiProducts

        // Set total from API (for non-anchor collections, this will be accurate)
        // For anchor collections, the complete dataset fetch will handle totals correctly
        this.totalProducts = apiTotal
      }

      this.facets = response.queryResults[0].filters
    })

    eventBus.subscribe('api:facets:fetched', response => {
      this.facets = response
    })

    eventBus.subscribe('pagination:control:load-more', () => {
      this.offset += window.theme.collection.limit
      this.currentPage += 1

      // Update URL with current page parameter
      this.updateUrlParameter('page', this.currentPage)

      // Notify pagination controls that page state changed
      window.theme.category.eventBus.publish('state:pagination:updated')
    })

    eventBus.subscribe('pagination:control:load-previous', () => {
      if (this.currentPage === 2) {
        this.offset = 0
      } else {
        this.offset -= window.theme.collection.limit
      }
      this.currentPage -= 1

      this.updateUrlParameter('page', this.currentPage)

      // Notify pagination controls that page state changed
      window.theme.category.eventBus.publish('state:pagination:updated')
    })

    eventBus.subscribe('state:sort:updated', value => {
      this.activeSort = value
      this.updateUrlParameter('sort_by', this.activeSort)
    })

    eventBus.subscribe('facets-list:apply-filter', ({ facet, option }) => {
      const lowerCaseFacetKey = facet.toLowerCase()
      const existingFacetIndex = this.activeFacets.findIndex(
        activeFacet => activeFacet.key === lowerCaseFacetKey,
      )

      let updatedFacets

      if (existingFacetIndex !== -1) {
        updatedFacets = this.activeFacets.map((activeFacet, index) => {
          if (index === existingFacetIndex) {
            return {
              ...activeFacet,
              values: [...activeFacet.values, option],
            }
          }
          return activeFacet
        })
      } else {
        updatedFacets = [
          ...this.activeFacets,
          {
            key: lowerCaseFacetKey,
            values: [option],
            singleSelect: false,
          },
        ]
      }

      this.activeFacets = updatedFacets
      this.updateFiltersUrlParameter()
    })

    eventBus.subscribe('facets-list:remove-filter', ({ facet, option }) => {
      const lowerCaseFacetKey = facet.toLowerCase()
      let updatedFacets = this.activeFacets

      if (lowerCaseFacetKey === 'klevu_price') {
        updatedFacets = this.activeFacets.filter(
          activeFacet => activeFacet.key !== lowerCaseFacetKey,
        )

        this.activeFacets = updatedFacets
        this.updateFiltersUrlParameter()

        window.theme.category.eventBus.publish('state:activeFacets:removed')
        window.theme.category.eventBus.publish('state:resetPriceRange')

        return
      }

      const facetIndex = this.activeFacets.findIndex(
        activeFacet => activeFacet.key === lowerCaseFacetKey,
      )

      if (facetIndex !== -1) {
        const targetFacet = this.activeFacets[facetIndex]

        const newValues = targetFacet.values.filter(val => val !== option)

        if (newValues.length === 0) {
          updatedFacets = this.activeFacets.filter(
            (_, index) => index !== facetIndex,
          )
        } else if (newValues.length < targetFacet.values.length) {
          updatedFacets = this.activeFacets.map((activeFacet, index) => {
            if (index === facetIndex) {
              return { ...activeFacet, values: newValues }
            }
            return activeFacet
          })
        }
      }

      this.activeFacets = updatedFacets
      this.updateFiltersUrlParameter()

      window.theme.category.eventBus.publish('state:activeFacets:removed')
    })

    eventBus.subscribe('facets-list:remove-all-filters', () => {
      this.activeFacets = []
      this.updateFiltersUrlParameter()

      window.theme.category.eventBus.publish('state:resetPriceRange')
      window.theme.category.eventBus.publish('state:activeFacets:removed')
    })

    eventBus.subscribe('price-range:change', ({ min, max }) => {
      const existingFacetIndex = this.activeFacets.findIndex(
        activeFacet => activeFacet.key === 'klevu_price',
      )

      let updatedFacets

      if (existingFacetIndex !== -1) {
        // If the facet already exists, update its values
        updatedFacets = this.activeFacets.map((activeFacet, index) => {
          if (index === existingFacetIndex) {
            return {
              ...activeFacet,
              values: [min, max],
            }
          }
          return activeFacet
        })
      } else {
        // If the facet doesn't exist, add a new one
        updatedFacets = [
          ...this.activeFacets,
          { key: 'klevu_price', values: [min, max], singleSelect: false },
        ]
      }

      this.activeFacets = updatedFacets
      this.updateFiltersUrlParameter()
    })
  }

  get products () {
    return this.state.products
  }

  get offset () {
    return this.state.pagination.offset
  }

  get totalProducts () {
    return this.state.totalProducts
  }

  get currentPage () {
    return this.state.pagination.currentPage
  }

  get activeSort () {
    return this.state.activeSort
  }

  get facets () {
    return this.state.facets
  }

  set facets (facets) {
    this.state.facets = facets
    window.theme.category.eventBus.publish('state:facets:updated', facets)
  }

  set products (products) {
    // If bundle anchors aren't ready yet, store products as pending
    if (
      !this.bundleAnchorsReady &&
      window.theme.collection?.bundle?.anchorCardsEnabled
    ) {
      this.pendingProducts = products
      // Set products without bundle management for now
      this.state.products = products
      window.theme.category.eventBus.publish('state:products:updated', products)
      return
    }

    // Apply bundle card management before setting state
    const managedProducts = this.applyBundleCardManagement(products)
    this.state.products = managedProducts

    window.theme.category.eventBus.publish(
      'state:products:updated',
      managedProducts,
    )
  }

  applyBundleCardManagement (products) {
    const enableAnchorCards =
      window.theme.collection?.bundle?.anchorCardsEnabled === true
    const anchors = window.theme.collection?.bundle?.anchors || []

    // If anchors haven't loaded yet or there are no anchors, return products as-is
    if (!this.bundleAnchorsReady || anchors.length === 0) {
      return products
    }

    const variantIdsToHide = []

    anchors.forEach(anchor => {
      if (!enableAnchorCards) {
        // When anchor cards are disabled, hide anchor variant IDs (show bundled products)
        const anchorVariantIds = anchor.anchorVariantIds || []
        variantIdsToHide.push(...anchorVariantIds.map(id => String(id)))
      } else {
        // When anchor cards are enabled, hide bundled product variant IDs (show anchor cards)
        const bundledProducts = anchor.bundledProducts || []
        bundledProducts.forEach(bundledProduct => {
          const bundledVariantIds = bundledProduct.variantIds || []
          variantIdsToHide.push(...bundledVariantIds.map(id => String(id)))
        })
      }
    })

    // Remove products that match the variant IDs to be hidden
    const filteredProducts = products.filter(
      product => !variantIdsToHide.includes(String(product.id)),
    )

    // If products were actually filtered, update the total count immediately
    if (filteredProducts.length !== products.length) {
      this.state.totalProducts = filteredProducts.length
      window.theme.category.eventBus.publish(
        'state:totalProducts:updated',
        filteredProducts.length,
      )
    }

    return filteredProducts
  }

  set totalProducts (totalProducts) {
    // Always use the API total count for accurate pagination
    // Bundle management only affects displayed products per page, not total count
    this.state.totalProducts = totalProducts
    window.theme.category.eventBus.publish(
      'state:totalProducts:updated',
      totalProducts,
    )
  }

  set offset (offset) {
    this.state.pagination.offset = offset
    window.theme.category.eventBus.publish('state:offset:updated', offset)
  }

  set currentPage (currentPage) {
    this.state.pagination.currentPage = currentPage
    window.theme.category.eventBus.publish(
      'state:currentPage:updated',
      currentPage,
    )
  }

  set activeSort (activeSort) {
    this.state.activeSort = activeSort
    window.theme.category.eventBus.publish(
      'state:activeSort:updated',
      activeSort,
    )
  }

  set activeFacets (facets) {
    this.state.activeFacets = facets

    if (this.currentPage > 1) {
      this.offset = 0
      this.currentPage = 1
      this.updateUrlParameter('page', null)
    }

    window.theme.category.eventBus.publish('state:activeFacets:updated', facets)
  }

  get activeFacets () {
    return this.state.activeFacets
  }
}

window.theme.category.stateLayer = new CategoryStateLayer()
